import type { Papel } from './tipos';

/**
 * 0132 · O PAPEL DE POSTO PARCEIRO E A CÓPIA DE PAPEL (decisão 58) — as
 * regras puras que a mesa e o celular leem.
 *
 * Pedido do dono (30/10/2026): o posto parceiro só tem acesso ao lançamento de
 * combustível, com o posto dele e o externo já escolhidos, "bem fechado e bem
 * parametrizado"; e uma forma de DUPLICAR os papéis para configurar o próximo
 * posto (e os próximos colaboradores) a partir de um pronto.
 *
 * O banco é quem garante (migração 0132): o papel de parceiro só aceita as
 * três permissões abaixo e a concessão dele exige o fornecedor. A tela só não
 * oferece o que o banco recusaria.
 */

/** As únicas permissões de um papel de parceiro: as do lançamento de combustível. */
export const PERMISSOES_DE_PARCEIRO = ['abastecimento.registrar', 'combustivel.ler', 'equipamento.ler'] as const;

export function ehPermissaoDeParceiro(codigo: string): boolean {
  return (PERMISSOES_DE_PARCEIRO as readonly string[]).includes(codigo);
}

/** O que a escolha de permissões oferece ao papel: no parceiro, só as do lançamento. Pura. */
export function permissoesOferecidas<T extends { readonly codigo: string }>(
  papel: Pick<Papel, 'parceiro'>,
  disponiveis: readonly T[],
): readonly T[] {
  return papel.parceiro === true ? disponiveis.filter((p) => ehPermissaoDeParceiro(p.codigo)) : disponiveis;
}

/** O nome que a cópia sugere. O nome do papel vai até 120 caracteres no servidor. Pura. */
export function nomeDaCopia(nome: string): string {
  const sufixo = ' (cópia)';
  return `${nome.trim().slice(0, 120 - sufixo.length)}${sufixo}`;
}

/**
 * O FORNECEDOR NA CONCESSÃO do papel à pessoa. Pura.
 *   - papel da empresa: sem fornecedor (o alcance é filial, obra ou contrato);
 *   - papel de UM posto: o fornecedor vem do papel, fixo;
 *   - papel de parceiro genérico: o fornecedor é escolhido, e é obrigatório.
 */
export type FornecedorDaConcessao =
  | { readonly modo: 'nenhum' }
  | { readonly modo: 'fixo'; readonly id: string; readonly nome: string }
  | { readonly modo: 'escolher' };

export function fornecedorDaConcessao(
  papel: Pick<Papel, 'parceiro' | 'fornecedor_id' | 'fornecedor_nome'> | null | undefined,
): FornecedorDaConcessao {
  if (!papel || papel.parceiro !== true) return { modo: 'nenhum' };
  if (papel.fornecedor_id) return { modo: 'fixo', id: papel.fornecedor_id, nome: papel.fornecedor_nome ?? 'Fornecedor do papel' };
  return { modo: 'escolher' };
}

/** O fornecedor do papel escrito: "POSTO MARQUES" ou "Escolhido na concessão". Pura. */
export function fornecedorEscrito(papel: Pick<Papel, 'fornecedor_id' | 'fornecedor_nome'>): string {
  return papel.fornecedor_id ? (papel.fornecedor_nome ?? 'Fornecedor do papel') : 'Escolhido na concessão';
}

/** A trava do papel de parceiro, escrita: ela não se edita, vem do papel. */
export const TRAVA_DO_PARCEIRO = 'Só externo, no posto do fornecedor: definido pelo papel de parceiro.';
